<template>
  <div>
    <SelectExistingDocument
      v-if="isExistingState"
      :replacement="replacement"
      :show-exports="showExports"
      @attach="attachExistingDocuments"
      @back="backToNewDocument"
    />

    <!-- Using v-show instead of v-else because there are events attached to DOM by the caller -->
    <div v-show="isInitialState">
      <div v-if="showFileManageArea" class="sf-pad-b-3">
        <div
          class="sf-grid sf-grid-fluid sf-pad-l-3 sf-pad-r-3 sf-pad-t-3"
          data-behaviour="document-select-file-collection-container"
        >
          <div class="sf-row.sf-row-center">
            <div class="sf-col sf-xs-12 sf-pad-l-0 sf-pad-r-0">
              <div class="sf-tab">
                <div data-behaviour="document-select-file-collection" />
                <div data-behaviour="document-select-in-progress-uploads" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <MultiFileDirectUploader
        v-if="!disabled"
        :existing-files="files"
        :accept="accept"
        :on-file-added="onFileAdded"
        :on-file-uploaded="onFileUploaded"
        :on-file-removed="onFileRemoved"
        :on-file-upload-progress="onFileUploadProgress"
      >
        <DocumentsUploaderText
          :variant="variant"
          :show-attach-external-link="canToggleToExternalLink"
          @attach-external-link="handleAttachExternalLinkClick"
          @select-existing="selectExistingFileClick"
        >
          <template #post-script>
            <slot name="post-script" />
          </template>
        </DocumentsUploaderText>
      </MultiFileDirectUploader>
      <DocumentsUploaderText
        v-else
        :variant="variant"
        :disabled="true"
        :show-attach-external-link="canToggleToExternalLink"
        @attach-external-link="handleAttachExternalLinkClick"
      >
        <template #post-script>
          <slot name="post-script" />
        </template>
      </DocumentsUploaderText>

      <div v-if="showControls" class="sf-grid sf-pad-3">
        <div class="sf-row sf-row-center">
          <div class=".sf-col.sf-xs-12">
            <div class="sf-level sf-level-center">
              <SfButtonDs
                data-behaviour="document-select-file-collection-download"
                style="display: none"
              >
                {{ I18n.t('common.download_all') }}
              </SfButtonDs>
              <div
                v-if="!replacement"
                data-behaviour="document-select-file-collection-save-container document-select-hide-when-locked"
              >
                <SfButtonDs
                  variant="primary"
                  data-behaviour="document-select-file-collection-save"
                >
                  {{ I18n.t('common.save') }}
                </SfButtonDs>
                <SfButtonDs
                  variant="primary"
                  disabled
                  data-behaviour="document-select-file-collection-save-disabled"
                  style="display: none"
                >
                  {{ I18n.t('common.save') }}
                </SfButtonDs>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ExternalLinkManager
      v-if="isDocumentLinkState && supportsExternalLinks"
      :id="externalLink?.id"
      :name="externalLink?.name"
      :url="externalLink?.url"
      @add-link="handleAttachExternalLink"
      @cancel="handleAttachExternalLinkCancel"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'

import { documentUiStates } from '../constants'
import ExternalLinkManager from '@app/application/external-links/components/external-link-manager.vue'
import DocumentsUploaderText from './documents-uploader-text.vue'
import { isFeatureSupported } from '@app/adapters/silverfin'
import MultiFileDirectUploader from '@app/shared/vue/components/direct-upload/multi-file-direct-uploader'
import SelectExistingDocument from '@app/application/reconciliation/sections/select-existing-document'
import { SfButtonDs } from '@silverfin/ui-components'

import { useDocumentUploadStore } from '../stores/document-upload'

export default {
  name: 'DocumentsUploader',

  components: {
    ExternalLinkManager,
    DocumentsUploaderText,
    MultiFileDirectUploader,
    SelectExistingDocument,
    SfButtonDs,
  },

  props: {
    accept: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    replacement: {
      type: Boolean,
      required: false,
      default: false,
    },
    onFileUploaded: {
      type: Function,
      required: false,
      default: () => {},
    },
    onFileRemoved: {
      type: Function,
      required: false,
      default: () => {},
    },
    onFileAttached: {
      type: Function,
      required: false,
      default: () => {},
    },
    onFileAdded: {
      type: Function,
      required: false,
      default: () => {},
    },
    onFileUploadProgress: {
      type: Function,
      required: false,
      default: () => {},
    },
    onLinkAttached: {
      type: Function,
      required: false,
      default: () => {},
    },
    showAttachExternalLink: {
      type: Boolean,
      required: false,
      default: true,
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    showExports: {
      type: Boolean,
      required: false,
      default: true,
    },
    showFileManageArea: {
      type: Boolean,
      required: false,
      default: true,
    },
    variant: {
      type: String,
      required: false,
      default: 'default',
    },
  },

  emits: ['select-existing-toggle'],

  setup() {
    const supportsExternalLinks = isFeatureSupported('supports_external_links')

    return {
      supportsExternalLinks,
    }
  },

  data: function () {
    return {
      files: this.files || {},
    }
  },

  computed: {
    ...mapState(useDocumentUploadStore, ['activeState', 'externalLink']),
    isExistingState() {
      return this.activeState === documentUiStates.EXISTING
    },
    isInitialState() {
      return this.activeState === documentUiStates.INITIAL
    },
    isDocumentLinkState() {
      return this.activeState === documentUiStates.DOCUMENT_LINK
    },
    canToggleToExternalLink() {
      return this.showAttachExternalLink && this.supportsExternalLinks
    },
  },

  methods: {
    ...mapActions(useDocumentUploadStore, [
      'activateState',
      'clearExternalLinkAttributes',
    ]),
    handleAttachExternalLinkClick() {
      this.activateState(documentUiStates.DOCUMENT_LINK)
    },
    handleAttachExternalLink(links) {
      if (this.disabled) {
        return
      }

      this.clearExternalLinkAttributes()
      this.onLinkAttached(links)
      this.activateState(documentUiStates.INITIAL)
    },
    handleAttachExternalLinkCancel() {
      this.activateState(documentUiStates.INITIAL)
    },
    selectExistingFileClick() {
      this.activateState(documentUiStates.EXISTING)
      this.$emit('select-existing-toggle', true)
    },
    backToNewDocument() {
      this.activateState(documentUiStates.INITIAL)
      this.$emit('select-existing-toggle', false)
    },
    attachExistingDocuments(entries) {
      if (this.disabled) {
        return
      }

      this.onFileAttached(entries)
      this.activateState(documentUiStates.INITIAL)
      this.$emit('select-existing-toggle', false)
    },
  },
}
</script>
